import React, { useEffect, useRef, useState } from "react";
import type { Match, ScoreMap } from "../../../shared/types";

import * as random from "core/src/core/util/random.js";
import * as scoreUtil from "core/src/core/score/score-util.js";
import * as gsf from "core/src/core/game-state-functions.js";

import PlayerSteeringListener from "./player-steering-listener.js";
import TouchSteeringOverlay from "./TouchSteeringOverlay";
import LocalGameHandler from "../../../shared/local-game-handler.js";
import GameCanvas from "../../../game-canvas/GameCanvas";
import Score from "./Score";
import GameHud from "./GameHud";
import { matchPlaystyle } from "../../setup/presets";
// TEMP: power-up boxes removed from the side gutter; to be replaced with rings
// around the worm head (or similar). Re-enable EffectsHud when that lands.
// import EffectsHud from "./EffectsHud";
import GameLayout from "./GameLayout";
import { useIsTouch } from "../../../shared/useResponsive";
import Countdown from "../../../game-canvas/overlays/Countdown";
import RespawnCountdown from "../../../game-canvas/overlays/RespawnCountdown";
import SpawnTags from "../../../game-canvas/overlays/SpawnTags";
import FirstRunHints from "../../../game-canvas/overlays/FirstRunHints";
import { useFirstRun, useHintPhase } from "../../../shared/first-run";
import { useAuth } from "../../../shared/auth";
import { introSchedule, introLeaders } from "../../../shared/round-intro";
import musicController from "../../../audio/music-controller";
import { computeGameAudio } from "../../../audio/game-audio";
import { updateLocalSetupPlayers } from "../../../shared/local-setup-store";
import { roundWinners } from "../../../shared/round-winners";

import styles from "../play.module.scss";
import { useWakeLock } from "../../../shared/useWakeLock";

const playerSteeringListener = PlayerSteeringListener();

interface Props {
  match: Match;
  // Threaded by App but unused here (GameCanvas doesn't read it).
  overlay?: any;
  onGameOverAction?: (lapGame?: any) => void;
  onLeaveAction?: () => void;
  isLocal?: boolean;
  // false = the toolbar's leave exits at once (the shop's try-out match).
  confirmLeave?: boolean;
  // Replaces the info rail entirely (see GameLayout.railOverride).
  railOverride?: React.ReactNode;
  // Called once with the core game object after it starts (the shop's try-out
  // uses it to enter a victory lap and watch for a crash).
  onGameCreated?: (game: any) => void;
}

export default function LocalGame(props: Props) {
  // the screen stays on while the game is up
  useWakeLock();
  const { match, isLocal } = props;
  const propsRef = useRef(props);
  propsRef.current = props;

  const [, force] = useState(0);
  const forceRender = () => force((t) => t + 1);

  const [startScore] = useState(() => {
    const rounds = match.matchState.roundsData;
    if (rounds.length > 0) {
      return rounds[rounds.length - 1].endScore;
    }
    // core is loosely-typed JS: getStartScore returns `{}` to TS, so pin it to ScoreMap.
    return scoreUtil.getStartScore(match.matchConfig.players) as ScoreMap;
  });
  const touch = useIsTouch();
  const roundNumber = match.matchState.roundsData.length + 1;
  // First-run hints (first-run.ts): new players get the steering walked
  // through during the first round's start phase (later rounds play like any
  // other). Two players on one phone are left alone (the zones are shared
  // then, and the intro would need four hands).
  const { user } = useAuth();
  const firstRun = useFirstRun(user?.id, roundNumber);

  const localGameRef = useRef<any>(null);

  // Read the latest onGameOver through a ref (the handler created below holds a
  // stable reference to it).
  // This device's players (local play: every human).
  const humanIds = () => propsRef.current.match.matchConfig.players.filter((p: any) => p.type === "human").map((p: any) => p.id);
  const onGameOverRef = useRef<() => void>(() => {});
  onGameOverRef.current = () => {
    // The round-over sting, or the win's when the one human at the keyboard took it
    // (with several humans on one device, someone here always wins).
    const humans = humanIds();
    const top = scoreUtil.createSortedList(scoreUtil.calculateRoundScore(localGameRef.current.gameState))[0];
    if (humans.length === 1 && top && top.score > 0 && top.id === humans[0]) musicController.play("round-win", 0.2); // (a beat after the last crash, as online)
    else musicController.playRoundEnd(0.2);
    // Score the round now (off the real result), then hand the live game to the game-over
    // screen so it can keep running it as a cosmetic victory lap during the countdown.
    propsRef.current.match.addFinishedGameState(localGameRef.current.gameState);
    propsRef.current.onGameOverAction?.(localGameRef.current.game);
  };

  // Create the local game once (synchronously, so the first render has gameState);
  // it's started in the mount effect below.
  const [localGame] = useState(() => {
    const seed = random.generateSeed();
    const game = match.prepareNextGame(seed);
    return LocalGameHandler({ game, onGameOver: () => onGameOverRef.current() });
  });
  localGameRef.current = localGame;

  function setupSteering() {
    playerSteeringListener.removeKeyListeners();
    propsRef.current.match.matchConfig.players.forEach((player: any) => {
      if (player.type === "human") {
        playerSteeringListener.addKeyListeners({
          left: player.left,
          right: player.right,
          onSteeringUpdate: (steering: number) =>
            gsf.setPlayerSteering(localGameRef.current.gameState, player.id, steering),
        });
      }
    });
  }

  function changePlayerKey(playerId: any, side: string, newKey: string) {
    const players = match.matchConfig.players;
    players.forEach((p: any) => {
      // free the key wherever a human currently holds it
      if (p.type === "human") {
        if (p.left === newKey) p.left = null;
        if (p.right === newKey) p.right = null;
      }
    });
    const target = players.find((p: any) => p.id === playerId);
    if (target) {
      if (side === "left") target.left = newKey;
      else target.right = newKey;
    }
    setupSteering();
    if (isLocal) updateLocalSetupPlayers(players);
    forceRender();
  }

  function onTouchSteeringUpdate(playerId: any, steering: number) {
    gsf.setPlayerSteering(localGameRef.current.gameState, playerId, steering);
  }

  function leaveMatch() {
    localGameRef.current.stop();
    propsRef.current.onLeaveAction?.();
  }

  // Dev builds: expose live handles so instrumentation (e.g. input-latency
  // measurement from the console) can reach the gameState, mirroring the online
  // screen's ?debug=1 hook. Stripped from production by the DEV gate.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    (window as any).__achtungLocal = {
      gs: () => localGameRef.current?.gameState,
      youIds: () => propsRef.current.match.matchConfig.players.filter((p: any) => p.type === "human").map((p: any) => p.id),
      players: () => propsRef.current.match.matchConfig.players,
    };
    return () => { delete (window as any).__achtungLocal; };
  }, []);

  useEffect(() => {
    localGame.start();
    setupSteering();
    propsRef.current.onGameCreated?.(localGameRef.current.game);
    musicController.setGameAudioProvider(() => {
      const lg = localGameRef.current;
      return lg ? computeGameAudio(lg.gameState, humanIds()) : null;
    });
    return () => {
      playerSteeringListener.removeKeyListeners();
      musicController.setGameAudioProvider(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const game = localGame;
  const players = match.matchConfig.players;

  const humans = players.filter((p: any) => p.type === "human");
  const youIds = humans.map((p: any) => p.id);
  // The round-start roll call schedule (round-intro.ts): shared by the board
  // (head fade-in + tags) and the rail (row intro), so they land on the same beats.
  const schedule = introSchedule(players, startScore, game.gameState);
  const delays = schedule.delays;
  const leaderIds = introLeaders(players, startScore);
  const youKeys = Object.fromEntries(humans.map((h: any) => [h.id, touch ? null : { left: h.left, right: h.right }]));
  const touchHints = firstRun && touch && humans.length === 1;
  // keys tutorial: no name tags on the board, only the key cards
  const keyHints = firstRun && !touch;
  const hint = useHintPhase(firstRun && (!touch || humans.length === 1), game.gameState, () => game.gameState.gameTime, touch);
  // two players on one phone: the board goes, each gets a strip at their end
  const couch = touch && humans.length >= 2;
  const scoreProps = {
    gameState: game.gameState,
    players,
    startScore,
    maxScore: match.matchConfig.maxScore,
    maxRounds: match.matchConfig.maxRounds,
    youIds,
    youKeys,
    markEliminated: true,
    roundIntro: true,
  };

  return (
    <GameLayout
      hud={
        <GameHud
          gameState={game.gameState}
          roundNumber={roundNumber}
          totalPlayers={players.length}
          maxScore={match.matchConfig.maxScore}
          maxRounds={match.matchConfig.maxRounds}
          players={players}
          winners={roundWinners(match.matchState.roundsData)}
        />
      }
      arena={
        <GameCanvas config={{ fullscreen: true, introDelay: (id: string) => delays[id] }} gameState={game.gameState} players={players}>
          <Countdown gameState={game.gameState} getRenderTime={() => game.gameState.gameTime} />
          <RespawnCountdown gameState={game.gameState} youIds={youIds} getRenderTime={() => game.gameState.gameTime} />
          {!keyHints && <SpawnTags gameState={game.gameState} players={players} schedule={schedule} leaderIds={leaderIds} getRenderTime={() => game.gameState.gameTime} youTag={touchHints ? { ids: youIds, label: "This is you" } : undefined} />}
          <FirstRunHints gameState={game.gameState} players={players} youIds={youIds} youKeys={youKeys} touch={touch} phase={hint} delays={delays} getRenderTime={() => game.gameState.gameTime} />
        </GameCanvas>
      }
      score={<Score {...scoreProps} />}
      couchStrips={couch ? { bottom: <Score {...scoreProps} only={humans[0].id} />, top: <Score {...scoreProps} only={humans[1].id} /> } : undefined}
      map={game.gameState.map}
      playstyle={matchPlaystyle(match.matchConfig)}
      onLeave={leaveMatch}
      confirmLeave={props.confirmLeave !== false}
      railOverride={props.railOverride}
      settingsPlayers={humans}
      onChangeKey={changePlayerKey}
      touchControls={touch ? <TouchSteeringOverlay players={players} onSteeringUpdate={onTouchSteeringUpdate} hint={hint === "keys" ? null : hint} /> : undefined}
      hideScoreCompact={couch}
      getLive={() => {
        const gs = localGameRef.current.gameState;
        return !!gs.gameActive && gs.gameTime >= (gs.rollCallTime || 0);
      }}
    />
  );
}
